import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

import { appWindow } from '../../utils/appWindow/AppWindow';

export interface LegacyContextState {
  scale: number;
  resX: number;
  resY: number;
  innerWidth: number;
  innerHeight: number;
  isMinPortrait: boolean;
  isMinLandscape: boolean;
  isLegacyMode: boolean;
}

const initialContextValue: LegacyContextState = {
  scale: 1,
  resX: 1920,
  resY: 1080,
  innerWidth: 1920,
  innerHeight: 1080,
  isMinPortrait: false,
  isMinLandscape: false,
  isLegacyMode: appWindow.isLegacyMode,
};
const LegacyContext = createContext(initialContextValue);

export const useLegacyContext = (): LegacyContextState => useContext(LegacyContext);

export const LegacyContextProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const [state, setState] = useState<LegacyContextState>(initialContextValue);

  useEffect(() => {
    if (!state.isLegacyMode) return;
    document.documentElement.dataset.isLegacyMode = 'true';
    const receiveMessage = (event: MessageEvent): void => {
      if (event.data?.type === 'LegacyResize') {
        setState({
          scale: event.data.scale,
          resX: event.data.resX,
          resY: event.data.resY,
          innerWidth: event.data.innerWidth,
          innerHeight: event.data.innerHeight,
          isMinPortrait: event.data.isMinPortrait,
          isMinLandscape: event.data.isMinLandscape,
          isLegacyMode: true,
        });
      }
    };
    window.addEventListener('message', receiveMessage);
    return (): void => {
      window.removeEventListener('message', receiveMessage);
      delete document.documentElement.dataset.isLegacyMode;
    };
  }, [state.isLegacyMode]);

  return <LegacyContext.Provider value={state}>{children}</LegacyContext.Provider>;
};
